import {
  POOLS_MAP,
  PoolName,
  TRANSACTION_TYPES,
  WCRO,
  CRO,
  PoolTypes,
} from "../constants"
import { addSlippage, subtractSlippage } from "../utils/slippage"
import { enqueueToast } from "../components/Toastify"
import { formatUnits, parseUnits } from "@ethersproject/units"
import {
  useLPTokenContract,
  useSwapContract,
  useWethContract,
} from "./useContract"

import { AppState } from "../state"
import { BigNumber } from "@ethersproject/bignumber"
import { GasPrices } from "../state/user"
import { NumberInputState } from "../utils/numberInputState"
import { formatDeadlineToNumber } from "../utils"
import { updateLastTransactionTimes } from "../state/application"
import { useActiveWeb3React } from "."
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { TransactionSummaryData } from "src/components/TransactionSummary"
import { useTransactionModal } from "src/providers/TransactionModalProvider"
import { useCheckAndApproveTokenForTrade } from "src/hooks/useCheckAndApproveTokenForTrade"
import { useInvalidateUserData } from "src/hooks/usePoolData"

interface ApproveAndWithdrawStateArgument {
  tokenFormState: { [symbol: string]: NumberInputState }
  withdrawType: string
  lpTokenAmountToSpend: BigNumber
}

function getSymbolKeyInState(symbol: string) {
  return symbol === WCRO.symbol ? CRO.symbol : symbol
}

function getTokenAmountInput(
  state: ApproveAndWithdrawStateArgument,
  symbol: string,
) {
  return state.tokenFormState[getSymbolKeyInState(symbol)].valueSafe
}

export function useApproveAndWithdraw(
  poolName: PoolName,
): (
  state: ApproveAndWithdrawStateArgument,
  summary: TransactionSummaryData,
) => Promise<void> {
  const dispatch = useDispatch()
  const swapContract = useSwapContract(poolName)
  const wcroContract = useWethContract(WCRO)
  const { account, chainId } = useActiveWeb3React()
  const { addTransactions } = useTransactionModal()
  const checkAndApproveTokenForTrade = useCheckAndApproveTokenForTrade()
  const { onInvalidateUserData } = useInvalidateUserData(poolName)
  const { gasStandard, gasFast, gasInstant } = useSelector(
    (state: AppState) => state.application,
  )
  const {
    slippageCustom,
    slippageSelected,
    gasPriceSelected,
    gasCustom,
    transactionDeadlineCustom,
    transactionDeadlineSelected,
    infiniteApproval,
  } = useSelector((state: AppState) => state.user)
  const lpTokenContract = useLPTokenContract(poolName)
  const POOL = POOLS_MAP[poolName]

  return async function approveAndWithdraw(
    state: ApproveAndWithdrawStateArgument,
    summary: TransactionSummaryData,
  ): Promise<void> {
    try {
      if (!account || !chainId) throw new Error("Wallet must be connected")
      if (!swapContract) throw new Error("Swap contract is not loaded")
      if (state.lpTokenAmountToSpend.isZero()) return
      if (lpTokenContract == null) return
      let gasPrice
      if (gasPriceSelected === GasPrices.Custom && gasCustom?.valueSafe) {
        gasPrice = gasCustom.valueSafe
      } else if (gasPriceSelected === GasPrices.Standard) {
        gasPrice = gasStandard
      } else if (gasPriceSelected === GasPrices.Instant) {
        gasPrice = gasInstant
      } else {
        gasPrice = gasFast
      }
      gasPrice = parseUnits(gasPrice?.toString() || "45", "gwei")
      const allowanceAmount =
        state.withdrawType === "IMBALANCE"
          ? addSlippage(
              state.lpTokenAmountToSpend,
              slippageSelected,
              slippageCustom,
            )
          : state.lpTokenAmountToSpend
      const token = POOL.lpToken
      await checkAndApproveTokenForTrade(
        lpTokenContract,
        swapContract.address,
        account,
        allowanceAmount,
        infiniteApproval,
        gasPrice,
        {
          onTransactionError: () => {
            throw new Error("Approval failed")
          },
        },
        token,
      )

      console.debug(
        `lpTokenAmountToSpend: ${formatUnits(state.lpTokenAmountToSpend, 18)}`,
      )
      const deadline = Math.round(
        new Date().getTime() / 1000 +
          60 *
            formatDeadlineToNumber(
              transactionDeadlineSelected,
              transactionDeadlineCustom,
            ),
      )
      let spendTransaction
      if (state.withdrawType === "ALL") {
        spendTransaction = async () =>
          await swapContract.removeLiquidity(
            state.lpTokenAmountToSpend,
            POOL.poolTokens.map(({ symbol }) =>
              subtractSlippage(
                BigNumber.from(getTokenAmountInput(state, symbol)),
                slippageSelected,
                slippageCustom,
              ),
            ),
            deadline,
          )
      } else if (state.withdrawType === "IMBALANCE") {
        spendTransaction = async () =>
          await swapContract.removeLiquidityImbalance(
            POOL.poolTokens.map(({ symbol }) =>
              getTokenAmountInput(state, symbol),
            ),
            addSlippage(
              state.lpTokenAmountToSpend,
              slippageSelected,
              slippageCustom,
            ),
            deadline,
          )
      } else {
        spendTransaction = async () =>
          await swapContract.removeLiquidityOneToken(
            state.lpTokenAmountToSpend,
            POOL.poolTokens.findIndex(
              ({ symbol }) =>
                getSymbolKeyInState(symbol) === state.withdrawType,
            ),
            subtractSlippage(
              BigNumber.from(
                getTokenAmountInput(state, state.withdrawType || ""),
              ),
              slippageSelected,
              slippageCustom,
            ),
            deadline,
          )
      }
      const transactions = [spendTransaction]
      if (POOL.type === PoolTypes.CRO) {
        const croAmount = BigNumber.from(getTokenAmountInput(state, CRO.symbol))
        if (croAmount.gt(0) && wcroContract) {
          const unwrapTxt = async () => {
            return await wcroContract.withdraw(croAmount)
          }
          transactions.push(unwrapTxt)
        }
      }

      await addTransactions(transactions, summary)
      onInvalidateUserData()
      dispatch(
        updateLastTransactionTimes({
          [TRANSACTION_TYPES.WITHDRAW]: Date.now(),
        }),
      )
      return Promise.resolve()
    } catch (e) {
      console.error(e)
      enqueueToast(
        "error",
        e instanceof Error
          ? e.message
          : "Liquidity removal failed, your tokens have been reverted",
        "Failed",
      )
    }
  }
}
